import { EventEmitter } from 'events';
import { flatten } from 'array-flatten';
/**
 * Base class for Loader-based object
 * @category loader
 * @author Geoffrey Mayeux
 */
export class Loader extends EventEmitter{
	constructor(){
		super();
		this.parallel	= true;
		this.waiting  	= new Set();
		this.pending  	= new Set();
		this.progresses	= new Map();
		this.loaded  	= new Map();
	}

	/**
	 * Get the current progress of the loading process
	 * @returns {float} the current progress
	 */
	get progress(){
		if(this.progresses.size != 0){
			let progress = 0;
			this.progresses.forEach(p => progress += p);
			return progress / this.progresses.size;
		}
		return 1;
	}

	/**
	 * Check whether the loader has completed loading all resources
	 * @returns {boolean} true if complete
	 */
	get complete(){
		return this.progress == 1;
	}

	/**
	 * Get the number of elements added to this loader
	 * @returns {integer} the size
	 */
	get size(){
		return this.waiting.size + this.pending.size + this.loaded.size;
	}

	/**
	 * Allows to retrieve the loaded content of an added item
	 * @param {*} item - the item to lookup
	 * @returns {*} the loaded content
	 */
	get(item){
		return this.loaded.get(item);
	}

	/**
	 * Check weither the given item has been added to the loader
	 * @param {*} item - the item to lookup
	 * @returns {boolean} true if the item has been added
	 */
	has(item){
		return this.waiting.has(item) || this.pending.has(item) || this.loaded.has(item);
	}

	/**
	 * Add elements to load
	 * @param {...*} items - the items to add
	 * @returns {this} the result of the given item loaded
	 */
	add(...items){
		items = flatten(items);
		items.forEach((item) => {
			if(this.accepts(item)
			&& !this.waiting.has(item)
			&& !this.pending.has(item)
			&& !this.loaded.has(item)){
				this.waiting.add(item);
				this.progresses.set(item, 0);
			}
		});

		return this;
	}

	delete(...items){
		items = flatten(items);
		items.forEach((item) => {
			this.waiting.delete(item);
			this.pending.delete(item);
			this.loaded.delete(item);
			this.progresses.delete(item);
		});

		return this;
	}

	/**
	 * Check wheter an item is waiting to be loaded
	 * @param {*} item - the item to check
	 * @returns {boolean} true if the item is waiting to be loaded
	 */
	isWaiting(item){
		return this.waiting.has(item);
	}
	/**
	 * Check wheter an item is currently loading
	 * @param {*} item - the item to check
	 * @returns {boolean} true if the item is currently loading
	 */
	isPending(item){
		return this.pending.has(item);
	}
	/**
	 * Check wheter an item is loaded
	 * @param {*} item - the item to check
	 * @returns {boolean} true if the item is loaded
	 */
	isLoaded(item){
		return this.loaded.has(item);
	}

	/**
	 * Check if the given item is supported by this loader
	 * @param {*} item - the item to check
	 * @returns {boolean} true if the item is supported
	 */
	accepts(item){
		return true;
	}

	/**
	 * Loads an item. You have to override this methods when you extends a loader in order to change the loading logic.
	 * @param {*} item - the item to load
	 * @returns {Promise} A promise resolved with the loaded content
	 * @protected
	 */
	async loadItem(item){
    	return item;
	}

	async allSettled(promises){
		return new Promise((resolve, reject) => {
			let count = promises.length;
			let done = 0;

			if(count == 0)
				return resolve();

			promises.forEach(promise => {
				promise
				.then(() => {
					done++;
				})
				.catch((e) => {
					done++;
					throw e;
				})
				.finally(() => {
					if(done == count){
						resolve();
					}
				});
			});
		});
	}

	async sequentialLoad(){
		if(this.waiting.size == 0)
			this.emit('progress', this.progress);
		
		while(this.waiting.size > 0){
			let item 	 = this.waiting.values().next().value;

			this.waiting.delete(item);
			this.pending.add(item);

			try{
				let resource = await this.loadItem(item);
				this.pending.delete(item);
				this.loaded.set(item, resource);
				this.progresses.set(item, 1);
				this.emit('progress', this.progress);
			}
			catch(e){
				this.pending.delete(item);
				this.loaded.set(item, null);
				this.progresses.set(item, 1);
				this.emit('progress', this.progress);
				throw e;
			}
		}
	}

	async parallelLoad(){
		let promises = [];
		if(this.waiting.size == 0)
			this.emit('progress', this.progress);

		while(this.waiting.size > 0){
			let item 	 = this.waiting.values().next().value;

			this.waiting.delete(item);
			this.pending.add(item);

			promises.push(this.loadItem(item)
			.then(resource => {
				this.pending.delete(item);
				this.loaded.set(item, resource);
				this.progresses.set(item, 1);
				this.emit('progress', this.progress);
			})
			.catch(e => {
				this.pending.delete(item);
				this.loaded.set(item, null);
				this.progresses.set(item, 1);
				this.emit('progress', this.progress);
				throw e;
			}));
		}
		return this.allSettled(promises);
	}

	/**
	 * Start loading items
	 * @param {...*} items - items to add to loading process
	 * @returns {Promise} A promise resolved with @type {boolean}, true when load has been started, false when loading has already started.
	 */
	async load(...items){
		this.add(...items);
		if(!this._loading){
			this._loading = true;
			if(this.parallel)
				await this.parallelLoad();
			else await this.sequentialLoad();
			this._loading = false;
			this.emit('complete', this.loaded);
			this.loaded.forEach(item => this.progresses.delete(item));
			return this.loaded;
		}
		return false;
	}

}
